import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { concatMap, delay } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DialogService } from '@shared/services/dialog/dialog.service';
import { TableauService } from 'src/app/@shared/services/tableau.service';
import { NavigationService } from '@app/@shared/services/navigation/navigation.service';
import { environment } from '@env/environment';
import { TableauConfig } from '@app/@shared/models/shared.models';
import { Roadmap, NavigationObjectType } from '@app/@shared/models/navigation/navigation.models';
import { EmbeddedURLRequestModel } from '@app/@shared/models/embedded-url-request.model';

@Component({
    selector: 'app-roadmap-tab-container',
    templateUrl: './roadmap-tab-container.component.html',
    styleUrls: ['./roadmap-tab-container.component.scss'],
    standalone: false
})
@UntilDestroy()
export class RoadmapTabContainerComponent implements OnInit {

  public tableauRoadmapConfig: TableauConfig;
  public isRetool = false;
  public retoolRoadmapConfig: EmbeddedURLRequestModel;
  public roadmapName = '';

  constructor(
    private dialogService: DialogService,
    private navigationService: NavigationService,
    private route: ActivatedRoute,
    private router: Router,
    private tableauService: TableauService,
  ) {}

  ngOnInit(): void {
    const moduleName = this.route.snapshot.data.module;
    combineLatest([
      this.route.parent.params,
      this.route.params
    ]).pipe(
      delay(500), // delay is for avoid feature flag flaky values on page load
      concatMap(([{ subscriptionId }, { roadmapName }]: any) => {
        this.roadmapName = roadmapName ?? '';
        return this.navigationService.getSubscription(subscriptionId, moduleName);
      }),
      untilDestroyed(this),
    ).subscribe((subscription) => {
      const retoolRoadmapsV2 = subscription.roadmaps.filter(
        (roadmap) => roadmap.dashboardType === NavigationObjectType.RETOOL && roadmap.roadmapType
      );
      const retoolRoadmaps = subscription.roadmaps.filter(
        (roadmap) => roadmap.dashboardType === NavigationObjectType.RETOOL && !roadmap.roadmapType
      );
      const tableauRoadmaps = subscription.roadmaps.filter(
        (roadmap) => roadmap.dashboardType === NavigationObjectType.TABLEAU
      );
      if (retoolRoadmapsV2.length > 0) {
        this.isRetool = true;
        this.loadRetoolRoadmap(retoolRoadmapsV2);
      } else if (retoolRoadmaps.length > 0) {
        this.isRetool = true;
        this.loadRetoolRoadmap(retoolRoadmaps);
      } else if (tableauRoadmaps.length > 0) {
        this.isRetool = false;
        this.loadTableauRoadmap(tableauRoadmaps);
      }
    });
  }

  private loadRetoolRoadmap(roadmaps: Array<Roadmap>): void {
    const roadmap = roadmaps.find(
      (rmap: Roadmap) => rmap.name.toLocaleLowerCase() === this.roadmapName.toLocaleLowerCase()
    );
    if (roadmap) {
      this.retoolRoadmapConfig = {
        appId: roadmap.dashboardEmbedString,
        metadata: {
          roadmapType: roadmap.roadmapType,
        }
      }
    } else {
      this.router.navigate([ '../../' ], { relativeTo: this.route });
    }
  }

  private loadTableauRoadmap(roadmaps: Array<Roadmap>): void {
    this.tableauService.getTableauTicket().pipe(untilDestroyed(this)).subscribe(
      ({ ticket }) => {
        const roadmap = roadmaps.find(
          (rmap: Roadmap) => rmap.name.toLocaleLowerCase() === this.roadmapName.toLocaleLowerCase()
        );
        if (roadmap) {
          this.tableauRoadmapConfig = {
            url: this.getTableauUrl(roadmap.dashboardEmbedString, ticket),
            height: environment.roadmapVizHeight,
            width: environment.roadmapVizWidth,
          };
        }
      },
      () => this.dialogService.openError(),
    );
  }

  private getTableauUrl(roadMapLink: string, ticket: string): string {
    const path = roadMapLink.split('views/').pop();
    const { roadmapUrl } = environment;
    return roadmapUrl
      .replace('<ticket>', ticket)
      .replace('<roadmapPath>', path);
  }

}
